import { CreditCard, PenTool } from 'lucide-react';
import { Label } from '~/components/ui/label';
import { Input } from '~/components/ui/input';
import { Separator } from '~/components/ui/separator';
import { InvoiceSummaryTables } from '~/components/InvoiceSummaryTables';
import { PaymentMethodSelector } from '~/components/PaymentMethodSelector';
import { PaymentInfoCard } from '~/components/PaymentInfoCard';
import { separateAndSortInvoices } from '~/utils/paymentUtils';

export function PaymentStep({
  invoiceSummary,
  paymentMethod,
  onPaymentMethodChange,
  signature,
  onSignatureChange,
  paymentSettings
}: any) {
  const { tuitionInvoices, dueNowTotal } = separateAndSortInvoices(invoiceSummary);
  const firstFuturePaymentDate = tuitionInvoices[0]?.dueDate;
  const acceptsOnlinePayments = paymentSettings?.acceptOnlinePayments;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-3">
          <CreditCard className="h-6 w-6 text-emerald-600" />
        </div>
        <h2 className="text-xl font-semibold">Payment & Signature</h2>
        <p className="text-muted-foreground">Complete your enrollment</p>
      </div>

      {/* Invoice Summary */}
      <InvoiceSummaryTables invoiceSummary={invoiceSummary} />

      <Separator />

      {/* Payment Method Selection */}
      {acceptsOnlinePayments && (
        <PaymentMethodSelector
          selectedMethod={paymentMethod}
          originalMethod={paymentMethod}
          onChange={onPaymentMethodChange}
          dueNowTotal={dueNowTotal}
          tuitionInvoices={tuitionInvoices}
          showFuturePaymentsInfo={false}
        />
      )}

      {!acceptsOnlinePayments && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
          Online payments are not enabled for this school. Please contact the school
          office to arrange payment.
        </div>
      )}

      {/* Automatic Payments Info */}
      {acceptsOnlinePayments && tuitionInvoices.length > 0 && paymentMethod && (
        <PaymentInfoCard
          paymentMethod={paymentMethod}
          tuitionInvoices={tuitionInvoices}
          firstFuturePaymentDate={firstFuturePaymentDate || ''}
        />
      )}

      <Separator />

      {/* Final Signature */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <PenTool className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="final-signature" className="text-base font-medium">
            Final Signature
          </Label>
        </div>
        <p className="text-sm text-muted-foreground">
          By typing your name below, you acknowledge that all information provided
          is accurate and you agree to all terms and conditions of enrollment,
          including the payment obligations listed above.
        </p>
        <Input
          id="final-signature"
          value={signature?.signedBy || ''}
          onChange={(e) => onSignatureChange({ ...signature, signedBy: e.target.value })}
          placeholder="Type your full legal name"
          className="font-serif text-lg italic max-w-md"
        />
      </div>
    </div>
  );
}

export default PaymentStep;
